/**
 * Browser Login Widget session handling.
 *
 * The Telegram Login Widget payload is verified SERVER-SIDE by
 * /api/auth/verify-telegram, which mints an opaque session token
 * (hash-stored in D1). This module keeps that token on the device and
 * attaches it to every same-origin /api request via the
 * X-ZiaWellNav-Web-Session header so the middleware can prove the
 * browser user's identity. Telegram Mini App sessions (signed initData)
 * and the native iOS session flow are unaffected.
 */

const WEB_SESSION_STORAGE_KEY = 'ziawellnav_web_session';
export const WEB_SESSION_HEADER = 'X-ZiaWellNav-Web-Session';

// Fallback for storage-blocked contexts (private mode edge cases).
let memorySession: StoredWebSession | null = null;
let storageUnavailable = false;

export interface StoredWebSession {
  token: string;
  expiresAt: string;
}

function readStoredSession(): StoredWebSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(WEB_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWebSession;
    if (!parsed?.token || typeof parsed.token !== 'string') return null;
    if (parsed.expiresAt && (!Number.isFinite(Date.parse(parsed.expiresAt)) || Date.parse(parsed.expiresAt) <= Date.now())) {
      localStorage.removeItem(WEB_SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getWebSession(): StoredWebSession | null {
  const session = storageUnavailable ? memorySession : readStoredSession();
  if (session?.expiresAt && (!Number.isFinite(Date.parse(session.expiresAt)) || Date.parse(session.expiresAt) <= Date.now())) return null;
  return session;
}

export function getWebSessionToken(): string | null {
  return getWebSession()?.token || null;
}

function activeToken(): string | null {
  return getWebSessionToken();
}

export function hasActiveWebSession(): boolean {
  return Boolean(activeToken());
}

export function storeWebSession(token: string, expiresAt: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(WEB_SESSION_STORAGE_KEY, JSON.stringify({ token, expiresAt } satisfies StoredWebSession));
    storageUnavailable = false;
  } catch {
    storageUnavailable = true;
    // Storage full/blocked — retain this session only for the page lifetime.
  }
  memorySession = { token, expiresAt };
}

export function clearWebSession(): void {
  memorySession = null;
  storageUnavailable = false;
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(WEB_SESSION_STORAGE_KEY);
  } catch { storageUnavailable = true; }
}

function isSameOriginApiRequest(input: RequestInfo | URL): boolean {
  try {
    const raw = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
    if (!raw) return false;
    if (raw.startsWith('/api/') || raw === '/api') return true;
    if (typeof window !== 'undefined') {
      const parsed = new URL(raw, window.location.origin);
      return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api');
    }
    return false;
  } catch {
    return false;
  }
}

let patched = false;

/**
 * Wrap window.fetch so same-origin /api requests carry the web session header
 * when a session exists. Installed once at app startup; composes with other
 * fetch wrappers (offline simulation, native routing) because it only ADDS a
 * header and always delegates.
 */
export function installWebSessionFetchPatch(): void {
  if (patched || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  patched = true;
  const baseFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const token = activeToken();
      if (token && isSameOriginApiRequest(input)) {
        const headers = new Headers(
          init?.headers || (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has(WEB_SESSION_HEADER)) {
          headers.set(WEB_SESSION_HEADER, token);
        }
        return baseFetch(input as RequestInfo, { ...init, headers });
      }
    } catch {
      // fall through to unmodified fetch
    }
    return baseFetch(input as RequestInfo, init);
  }) as typeof window.fetch;
}
